import React from "react";
import { registerExtension, EXTENSION_SORT_ORDER } from "@core/components/PipelineComponents/ExtensionProvider";
import { TooltipWrapper } from "@core/components/TooltipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { Switch } from "@core/components/ui/switch";
import { Label } from "@core/components/ui/label";
import { Input } from "@core/components/ui/Input";
import { useApi } from "@core/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider, Controller } from "react-hook-form";

const InvoicingComponent = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  const pluginName = "Invoicing";
  const settingsGroup = "InvoicingSettings";

  // Load settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["invoicingSettings", pluginName, settingsGroup],
    queryFn: () => api["/settings/{plugin}/{group}"](pluginName, settingsGroup),
  });

  const methods = useForm<any>({ 
    defaultValues: settings?.SettingValues ?? {} 
  });

  // Update form when settings load
  React.useEffect(() => {
    if (settings?.SettingValues) {
      methods.reset(settings.SettingValues);
    }
  }, [settings, methods]);

  // Mutation to save settings
  const saveSettingsMutation = useMutation({
    mutationFn: async (data: any) => {
      await api["/settings/{plugin}/{group}/update"](pluginName, settingsGroup, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoicingSettings", pluginName, settingsGroup] });
    },
  });

  const handleToggleChange = (checked: boolean) => {
    const currentValues = methods.getValues();
    const updatedData = {
      ...currentValues,
      AddInvoicesRoute: checked,
    };
    saveSettingsMutation.mutate(updatedData);
  };

  const handleInvoiceCheckoutToggleChange = (checked: boolean) => {
    const currentValues = methods.getValues();
    const updatedData = {
      ...currentValues,
      AllowInvoiceCheckout: checked,
    };
    saveSettingsMutation.mutate(updatedData);
  };

  const handleNewInvoiceBatchingToggleChange = (checked: boolean) => {
    const currentValues = methods.getValues();
    const intervalRaw = currentValues.NewInvoiceBatchingIntervalDays;
    const interval = Math.max(1, parseInt(String(intervalRaw), 10) || 1);
    const updatedData = {
      ...currentValues,
      NewInvoiceBatchingEnabled: checked,
      NewInvoiceBatchingIntervalDays: interval,
    };
    if (checked) {
      methods.setValue("NewInvoiceBatchingIntervalDays", interval);
    }
    saveSettingsMutation.mutate(updatedData);
  };

  const handleMonthlyStatementEmailToggleChange = (checked: boolean) => {
    const currentValues = methods.getValues();
    const updatedData = {
      ...currentValues,
      MonthlyStatementEmailEnabled: checked,
    };
    saveSettingsMutation.mutate(updatedData);
  };

  const handleNewInvoiceBatchingIntervalCommit = () => {
    const currentValues = methods.getValues();
    const raw = currentValues.NewInvoiceBatchingIntervalDays;
    const n = Math.max(1, parseInt(String(raw), 10) || 1);
    const updatedData = {
      ...currentValues,
      NewInvoiceBatchingIntervalDays: n,
    };
    methods.setValue("NewInvoiceBatchingIntervalDays", n);
    saveSettingsMutation.mutate(updatedData);
  };

  const handleMonthlyStatementGenerationToggleChange = (checked: boolean) => {
    const currentValues = methods.getValues();
    const updatedData = {
      ...currentValues,
      MonthlyStatementGenerationEnabled: checked,
    };
    saveSettingsMutation.mutate(updatedData);
  };

  const handleMonthlyStatementsTabToggleChange = (checked: boolean) => {
    const currentValues = methods.getValues();
    const updatedData = {
      ...currentValues,
      MonthlyStatementsTabEnabled: checked,
    };
    saveSettingsMutation.mutate(updatedData);
  };

  const globalToggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      api["/invoicing/autopay/global-toggle"]({ Enabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoicingSettings", pluginName, settingsGroup] });
    },
    onError: (_error: unknown, enabled: boolean) => {
      methods.setValue("AutoPayEnabled", !enabled);
    },
  });

  const handleAutoPayToggleChange = (checked: boolean) => {
    if (checked) {
      const daysRaw = methods.getValues("AutoPayDaysAfterGeneration");
      const days = Math.max(1, parseInt(String(daysRaw), 10) || 7);
      methods.setValue("AutoPayDaysAfterGeneration", days);
    }
    globalToggleMutation.mutate(checked);
  };

  const handleAutoPayDaysCommit = (): void => {
    const currentValues = methods.getValues();
    const raw = currentValues.AutoPayDaysAfterGeneration;
    const days = Math.max(1, parseInt(String(raw), 10) || 7);
    const updatedData = {
      ...currentValues,
      AutoPayDaysAfterGeneration: days,
    };
    methods.setValue("AutoPayDaysAfterGeneration", days);
    saveSettingsMutation.mutate(updatedData);
  };

  // Watch the main toggle value to conditionally show sub-option
  const addInvoicesRoute = methods.watch("AddInvoicesRoute");
  const newInvoiceBatchingEnabled = methods.watch("NewInvoiceBatchingEnabled");
  const autoPayEnabled = methods.watch("AutoPayEnabled");

  if (settingsLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Invoicing</h3>
        <div className="text-sm text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Invoicing</h3>
        <div className="text-sm text-muted-foreground">Settings not available.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Invoicing</h3>
      <p className="text-sm text-muted-foreground italic">
        This functionality needs clarification before implementing - eg. should the entire plugin be deregistered or can settings accomplish this
      </p>
      <FormProvider {...methods}>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Controller
              name="AddInvoicesRoute"
              control={methods.control}
              defaultValue={true}
              render={({ field }) => (
                <Switch
                  id="addInvoicesRoute"
                  checked={field.value ?? true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleToggleChange(checked);
                  }}
                  disabled={saveSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="addInvoicesRoute" className="cursor-pointer">
                Will your site use invoices?
              </Label>
              <TooltipWrapper
                tooltipContent="Invoices are records that represent outstanding balances customers will pay against."
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* Sub-option: Invoice Checkout - only show when main toggle is true */}
          {addInvoicesRoute && (
            <div className="ml-8 pl-4 border-l-2 border-muted space-y-2">
              <div className="flex items-center space-x-2">
                <Controller
                  name="AllowInvoiceCheckout"
                  control={methods.control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Switch
                      id="allowInvoiceCheckout"
                      checked={field.value ?? false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        handleInvoiceCheckoutToggleChange(checked);
                      }}
                      disabled={saveSettingsMutation.isPending}
                    />
                  )}
                />
                <div className="flex items-center gap-2">
                  <Label htmlFor="allowInvoiceCheckout" className="cursor-pointer">
                    Will your site allow invoice checkout?
                  </Label>
                  <TooltipWrapper
                    tooltipContent="Invoice checkout allows users to order products without paying today and pay against an invoice at a later date."
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="text-muted-foreground cursor-help"
                    />
                  </TooltipWrapper>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2 pt-4 mt-4 border-t border-muted">
            <div className="flex items-center space-x-2">
              <Controller
                name="NewInvoiceBatchingEnabled"
                control={methods.control}
                defaultValue={false}
                render={({ field }) => (
                  <Switch
                    id="newInvoiceBatchingEnabled"
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      handleNewInvoiceBatchingToggleChange(checked);
                    }}
                    disabled={saveSettingsMutation.isPending}
                  />
                )}
              />
              <div className="flex items-center gap-2">
                <Label htmlFor="newInvoiceBatchingEnabled" className="cursor-pointer">
                  Batch new invoice notification emails
                </Label>
                <TooltipWrapper
                  tooltipContent="When enabled, customers receive one email per interval listing all new invoices instead of a separate email per invoice. Per-invoice update emails are not sent while this is on."
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="text-muted-foreground cursor-help"
                  />
                </TooltipWrapper>
              </div>
            </div>

            {newInvoiceBatchingEnabled && (
              <div className="ml-8 pl-4 border-l-2 border-muted space-y-2">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="newInvoiceBatchingIntervalDays" className="cursor-pointer">
                      Days between batched emails
                    </Label>
                    <Controller
                      name="NewInvoiceBatchingIntervalDays"
                      control={methods.control}
                      defaultValue={1}
                      render={({ field }) => (
                        <Input
                          id="newInvoiceBatchingIntervalDays"
                          type="number"
                          min={1}
                          step={1}
                          className="w-28"
                          disabled={saveSettingsMutation.isPending}
                          value={field.value === undefined || field.value === null ? "" : String(field.value)}
                          onChange={(e) => {
                            const v = e.target.value;
                            field.onChange(v === "" ? "" : Number(v));
                          }}
                          onBlur={() => {
                            field.onBlur();
                            handleNewInvoiceBatchingIntervalCommit();
                          }}
                        />
                      )}
                    />
                  </div>
                  <TooltipWrapper
                    tooltipContent="Minimum days between sends (for example 1 = daily, 7 = weekly). The scheduler may run daily; this value controls how often emails actually go out."
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="text-muted-foreground cursor-help mb-2"
                    />
                  </TooltipWrapper>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2 pt-4 mt-4 border-t border-muted">
            <h4 className="text-sm font-medium text-foreground">Monthly statement emails</h4>
            <div className="flex items-center space-x-2">
              <Controller
                name="MonthlyStatementEmailEnabled"
                control={methods.control}
                defaultValue={false}
                render={({ field }) => (
                  <Switch
                    id="monthlyStatementEmailEnabled"
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      handleMonthlyStatementEmailToggleChange(checked);
                    }}
                    disabled={saveSettingsMutation.isPending}
                  />
                )}
              />
              <div className="flex items-center gap-2">
                <Label htmlFor="monthlyStatementEmailEnabled" className="cursor-pointer">
                  Send monthly statement emails
                </Label>
                <TooltipWrapper
                  tooltipContent="When enabled, after each customer's monthly statement PDF is generated, users under that account receive an email with a summary of outstanding invoices and the PDF attached. Requires monthly statement PDF generation to be enabled and running."
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="text-muted-foreground cursor-help"
                  />
                </TooltipWrapper>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <h4 className="text-base font-medium">Monthly Statement PDF</h4>
          <div className="flex items-center space-x-2">
            <Controller
              name="MonthlyStatementGenerationEnabled"
              control={methods.control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  id="monthlyStatementGenerationEnabled"
                  checked={field.value ?? false}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleMonthlyStatementGenerationToggleChange(checked);
                  }}
                  disabled={saveSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="monthlyStatementGenerationEnabled" className="cursor-pointer">
                Generate monthly statement PDFs
              </Label>
              <TooltipWrapper
                tooltipContent="When enabled, the monthly statement generation task can create statement PDFs for customers with outstanding invoices."
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>
          {addInvoicesRoute && (
            <div className="ml-0 mt-3 space-y-2">
              <div className="flex items-center space-x-2">
                <Controller
                  name="MonthlyStatementsTabEnabled"
                  control={methods.control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Switch
                      id="monthlyStatementsTabEnabled"
                      checked={field.value ?? false}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        handleMonthlyStatementsTabToggleChange(checked);
                      }}
                      disabled={saveSettingsMutation.isPending}
                    />
                  )}
                />
                <div className="flex items-center gap-2">
                  <Label htmlFor="monthlyStatementsTabEnabled" className="cursor-pointer">
                    Show Invoice Statements tab in user dashboard
                  </Label>
                  <TooltipWrapper
                    tooltipContent="When enabled, customers who use invoices will see an Invoice Statements tab on the dashboard to view and download monthly statement PDFs."
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="text-muted-foreground cursor-help"
                    />
                  </TooltipWrapper>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2 pt-4 mt-4 border-t border-muted">
          <h4 className="text-sm font-medium text-foreground">Auto Pay</h4>
          <div className="flex items-center space-x-2">
            <Controller
              name="AutoPayEnabled"
              control={methods.control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  id="autoPayEnabled"
                  checked={field.value ?? false}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleAutoPayToggleChange(checked);
                  }}
                  disabled={globalToggleMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="autoPayEnabled" className="cursor-pointer">
                Enable Auto Pay
              </Label>
              <TooltipWrapper
                tooltipContent="When enabled, customers can enroll in automatic payments from the Wallet tab. Eligible open invoices are captured after the configured number of days from the invoice generation date."
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {autoPayEnabled && (
            <div className="ml-8 pl-4 border-l-2 border-muted space-y-2">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="autoPayDaysAfterGeneration" className="cursor-pointer">
                    Days after invoice generation to capture
                  </Label>
                  <Controller
                    name="AutoPayDaysAfterGeneration"
                    control={methods.control}
                    defaultValue={7}
                    render={({ field }) => (
                      <Input
                        id="autoPayDaysAfterGeneration"
                        type="number"
                        min={1}
                        step={1}
                        className="w-28"
                        disabled={saveSettingsMutation.isPending}
                        value={field.value === undefined || field.value === null ? "" : String(field.value)}
                        onChange={(e) => {
                          const v = e.target.value;
                          field.onChange(v === "" ? "" : Number(v));
                        }}
                        onBlur={() => {
                          field.onBlur();
                          handleAutoPayDaysCommit();
                        }}
                      />
                    )}
                  />
                </div>
                <TooltipWrapper
                  tooltipContent="Open invoices with status Unpaid or Partially Paid are auto-paid on or after this many days from the invoice created date."
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="text-muted-foreground cursor-help mb-2"
                  />
                </TooltipWrapper>
              </div>
            </div>
          )}
        </div>
      </FormProvider>
    </div>
  );
};

registerExtension(
  "admin.system.sitesetup.siteconfiguration",
  InvoicingComponent,
  "Invoicing",
  false,
  EXTENSION_SORT_ORDER.HIGH + 50  // Display after Registration (HIGH) and before Payments (HIGH + 100)
);
